import * as yup from 'yup';
import moment from 'moment';
import {
  MortgageType,
  RealEstateDTO,
  RealEstateInvestmentType,
} from '../../tables/RealEstate/RealEstate.meta';
import { EarmarkDTO, OwnerModelDTO, OwnerModelFormDTO, OwnerTypeDTO } from '../../OtherAssets.meta';
import {
  EXCLUDE_OPT,
  getCustomBucketIndex,
} from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';

export const DATE_FE_FORMAT = 'MM/DD/YYYY';
export const DATE_BE_FORMAT = 'YYYY-MM-DD';

export interface RealEstateFormValues {
  name: string;
  investmentType: RealEstateInvestmentType | null;
  owner: string | null;
  marketValue: number | null;
  mortgageType: MortgageType | string | null;
  rentalIncome: number | null;
  assetGrowth: number | null;
  propertyTax: number | null;
  homeInsurance: number | null;
  monthlyHoa: number | null;
  saleYear: string | null;
  saleNetEarnings: number | null;
  incomeGrowth: number | null;
  mortgageId: string | null;
  mortgageBalance: number | null;
  mortgageInterestRate: number | null;
  mortgageMonthlyPayment: number | null;
  mortgageOriginationDate: string | null;
  mortgageExcludeFromDebtManagementGoal: boolean;
  mortgageMonthlyPmi: number | null;
  assetEarmarks: { value: string; label: string }[];
  customBucketIndex: number | null;
  isLiquid: boolean;
  includeInGrossEstate: boolean;
  orionManagementStyleId: number | null;
  ownerModel: OwnerModelFormDTO[];
}

export interface RealEstateFormDTO {
  Name: string;
  InvestmentType: RealEstateInvestmentType;
  Owner: string;
  MarketValue: number | null;
  MortgageType: MortgageType | string;
  YearlyRentalIncome: number | null;
  AssetGrowth: number | null;
  PropertyTaxPerYear: number | null;
  HomeInsurancePerYear: number | null;
  MonthlyHoa: number | null;
  SaleYear: string | null;
  IsSoldAtRetirement: boolean;
  SaleNetEarnings: number | null;
  IncomeGrowth: number | null;
  MortgageId: string | null;
  MortgageOutstandingBalance: number | null;
  MortgageInterestRate: number | null;
  MortgageMonthlyPayment: number | null;
  MortgageOriginationDate: string | null;
  MortgageExcludeFromDebtManagementGoal: boolean;
  MortgageMonthlyPmi: number | null;
  AssetEarmark: EarmarkDTO[];
  CustomBucketIndex: number | null;
  IsExcludedFromAssetBucketing: boolean;
  IsLiquid: boolean;
  IncludeInGrossEstate: boolean;
  OrionManagementStyleId: number | null;
  OwnerModel: OwnerModelDTO[];
}

export interface UpsertRealEstateDTO extends RealEstateFormDTO {
  clientId: string;
  Id: string | null;
  MortgageInstitutionId: string | number | null;
  MortgageInstitutionName: string | null;
}

interface DataToForm {
  data: RealEstateDTO | null;
  options: OtherAssetsDTO['Options'];
}

export const dataToForm = ({ data, options }: DataToForm): RealEstateFormValues =>
  data
    ? {
        name: data.Name,
        investmentType: data.InvestmentType,
        owner: data.Owner,
        marketValue: data.MarketValue,
        mortgageType: data.MortgageType,
        rentalIncome: data.YearlyRentalIncome,
        assetGrowth: data.AssetGrowth,
        propertyTax: data.PropertyTaxPerYear,
        homeInsurance: data.HomeInsurancePerYear,
        monthlyHoa: data.MonthlyHoa,
        saleYear:
          data.SaleYear !== null
            ? data.SaleYear.toString()
            : data.IsSoldAtRetirement
            ? 'At Retirement'
            : null,
        saleNetEarnings: data.SaleNetEarnings,
        incomeGrowth: data.IncomeGrowth,
        mortgageId: data.MortgageId,
        mortgageBalance: data.MortgageOutstandingBalance,
        mortgageInterestRate: data.MortgageInterestRate,
        mortgageMonthlyPayment: data.MortgageMonthlyPayment,
        mortgageOriginationDate: data.MortgageOriginationDate
          ? moment(data.MortgageOriginationDate).format(DATE_FE_FORMAT)
          : '',
        mortgageExcludeFromDebtManagementGoal: data.MortgageExcludeFromDebtManagementGoal,
        mortgageMonthlyPmi: data.MortgageMonthlyPmi,
        assetEarmarks: data.AssetEarmark.map(({ Type, Tooltip }) => ({
          value: Type,
          label: Tooltip,
        })),
        customBucketIndex: getCustomBucketIndex({
          orionManagementStyleId: data.OrionManagementStyleId,
          customBucketIndex: data.CustomBucketIndex,
          managementStyles: options.ManagementStyles,
          isExcludedFromAssetBucketing: false,
          assetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
        }),
        isLiquid: data.IsLiquid,
        includeInGrossEstate: data.IncludeInGrossEstate,
        orionManagementStyleId: data.OrionManagementStyleId,
        ownerModel: data.OwnerModel.map(owner => ({
          ...owner,
          Percentage: (owner.Percentage * 100).toFixed(2),
        })),
      }
    : {
        name: '',
        investmentType: null,
        owner: '',
        marketValue: null,
        mortgageType: null,
        rentalIncome: null,
        assetGrowth: null,
        propertyTax: null,
        homeInsurance: null,
        monthlyHoa: null,
        saleYear: null,
        saleNetEarnings: null,
        incomeGrowth: null,
        mortgageId: null,
        mortgageBalance: null,
        mortgageInterestRate: null,
        mortgageMonthlyPayment: null,
        mortgageOriginationDate: '',
        mortgageExcludeFromDebtManagementGoal: false,
        mortgageMonthlyPmi: null,
        assetEarmarks: [],
        customBucketIndex: null,
        isLiquid: options.RealEstateOptions.IsLiquid,
        includeInGrossEstate: true,
        orionManagementStyleId: null,
        ownerModel: [
          {
            Id: null,
            Name: options.RealEstateOptions.ownerOptions.People[0].Name,
            OwnerType: OwnerTypeDTO.Principal,
            Percentage: '100',
          },
        ],
      };

export const formToBody = (values: RealEstateFormValues): RealEstateFormDTO => ({
  Name: values.name,
  InvestmentType: values.investmentType ?? RealEstateInvestmentType.Personal,
  Owner: values.owner || '',
  MarketValue: values.marketValue,
  MortgageType:
    values.mortgageType === 'Existing' || values.mortgageType === null ? '' : values.mortgageType,
  YearlyRentalIncome: values.rentalIncome,
  AssetGrowth: values.assetGrowth === null ? 0 : values.assetGrowth,
  PropertyTaxPerYear: values.propertyTax,
  HomeInsurancePerYear: values.homeInsurance,
  MonthlyHoa: values.monthlyHoa,
  SaleYear: values.saleYear === 'At Retirement' ? null : values.saleYear,
  IsSoldAtRetirement: values.saleYear === 'At Retirement',
  SaleNetEarnings: values.saleNetEarnings,
  IncomeGrowth: values.incomeGrowth,
  MortgageId: values.mortgageId,
  MortgageOutstandingBalance: values.mortgageBalance,
  MortgageInterestRate: values.mortgageInterestRate,
  MortgageMonthlyPayment: values.mortgageMonthlyPayment,
  MortgageOriginationDate: values.mortgageOriginationDate
    ? moment(values.mortgageOriginationDate, DATE_FE_FORMAT).format(DATE_BE_FORMAT)
    : null,
  MortgageExcludeFromDebtManagementGoal: values.mortgageExcludeFromDebtManagementGoal,
  MortgageMonthlyPmi: values.mortgageMonthlyPmi,
  AssetEarmark: values.assetEarmarks.map(({ value, label }) => ({ Type: value, Tooltip: label })),
  CustomBucketIndex:
    values.customBucketIndex === EXCLUDE_OPT.value ? null : values.customBucketIndex,
  IsExcludedFromAssetBucketing: values.customBucketIndex === EXCLUDE_OPT.value,
  IsLiquid: values.isLiquid,
  IncludeInGrossEstate: values.includeInGrossEstate,
  OrionManagementStyleId: values.orionManagementStyleId,
  OwnerModel: values.ownerModel
    .filter(b => b.Name && b.Percentage)
    .map(owner => ({
      ...owner,
      Id: owner.Id ?? null,
      Name: owner.Name ?? '',
      OwnerType: owner.OwnerType ?? 0,
      Percentage: Number((parseFloat(owner.Percentage ?? '0') / 100).toFixed(4)),
    })),
});

export const REAL_ESTATE_FORM_SCHEMA = yup.object().shape({
  name: yup.string().required('Please provide a name for this property'),
  investmentType: yup
    .number()
    .nullable()
    .oneOf([RealEstateInvestmentType.Personal, RealEstateInvestmentType.Investment])
    .required('Please specify the real estate usage'),
  marketValue: yup
    .number()
    .nullable()
    .notOneOf([0], 'Please specify a valid market value')
    .required('Please provide a market value'),
  mortgageBalance: yup
    .number()
    .nullable()
    .when('mortgageType', {
      is: mortgageType =>
        mortgageType !== null && mortgageType.toString() !== '' && mortgageType !== 'Existing',
      then: yup
        .number()
        .nullable()
        .notOneOf([0], 'Please specify a valid mortgage outstanding balance')
        .required('Please specify the mortgage outstanding balance'),
    }),
  mortgageOriginationDate: yup
    .string()
    .nullable()
    .test('is-mortgage-origination-date-valid', 'Date is not valid', value => {
      if (!value) {
        return true;
      }

      const date = moment(value, DATE_FE_FORMAT, true);
      return date.isValid();
    }),
  ownerModel: yup
    .array()
    .test(
      'check-total',
      'The total ownership percentage must add up to 100%',
      (ownerModel, ctx) => {
        if (!ownerModel || !Array.isArray(ownerModel)) return false;
        if (ownerModel[0].Name === '') {
          return ctx.createError({
            path: 'ownerModel[0].Id',
            message: 'Please specify an account owner',
          });
        }
        const sum = ownerModel.reduce((acc, obj) => {
          if (obj.Name && obj.Percentage) {
            return acc + (parseFloat(obj.Percentage) || 0);
          }
          return acc;
        }, 0);
        if (sum !== 100) {
          return ctx.createError({
            path: 'ownerModel[0].Id',
            message: 'The total ownership percentage must add up to 100%',
          });
        }
        return sum === 100;
      },
    ),
});
